import { forwardRef, type SyntheticEvent, useMemo, useState } from "react";
import { MenuItem, type TextFieldProps } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { C_ON_SURFACE } from "../icon/Colors";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";
import { TextField } from "~/common/m3/TextField/TextField";

export interface SimpleSelectProps extends Omit<TextFieldProps<"outlined">, "variant"> {
  values: DropdownEntry<string | number>[];
  showFilter?: boolean;
  placeholder?: string;
  displayEmpty?: boolean;
  shrink?: boolean;
  onClose?: (e: SyntheticEvent) => void;
  onOpen?: (e: SyntheticEvent) => void;
  open?: boolean;
}

const useSearchBoxStyle = makeStyles({
  root: {
    padding: "10px 20px",
    minHeight: "50px",
    color: C_ON_SURFACE,
  },
});

export const SimpleSelectItem = MenuItem;

export const SimpleSelect = forwardRef<HTMLDivElement, SimpleSelectProps>((props: SimpleSelectProps, ref) => {
  const {
    values,
    children,
    placeholder,
    showFilter = false,
    displayEmpty,
    shrink,
    slotProps = {},
    onClose,
    onOpen,
    open,
    ...rest
  } = props;

  const { t } = useTranslation();
  const [filterText, setFilterText] = useState("");
  const elements = useMemo(() => {
    let filteredValues = values;
    if (filterText) {
      filteredValues = values.filter((element) => element.stringLabel?.includes(filterText));
    }
    return createSingleSelectItems(filteredValues, rest["data-test-id"]);
  }, [values, filterText]);

  const sbClasses = useSearchBoxStyle();

  return (
    <TextField
      select
      variant={"outlined"}
      {...rest}
      slotProps={{
        ...slotProps,
        inputLabel: {
          shrink,
        },
      }}
      /*
       * Unfortunately using slotProps.select.open does not work inside the project for some reason.
       * It DOES work inside storybook though, why?
       */
      SelectProps={{
        onClose,
        onOpen,
        displayEmpty,
        renderValue: (value) => {
          if (value === "" || value === null || value === undefined) {
            return placeholder;
          }
          return values.find((item) => item.value === value)?.label ?? undefined;
        },
        open,
      }}
      ref={ref}
    >
      {showFilter && (
        <div className={sbClasses.root}>
          <TextField
            placeholder={t("generic.searchHere")}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            value={filterText}
            onClick={PREVENT_CLOSE}
          />
        </div>
      )}
      {placeholder && (
        <SimpleSelectItem value={""} data-test-id={rest["data-test-id"] + "-placeholder"}>
          {placeholder}
        </SimpleSelectItem>
      )}
      {elements}
      {children}
    </TextField>
  );
});

export const createSingleSelectItems = (items: DropdownEntry<string | number>[], testId?: string) =>
  items.map((item) => {
    const childId = testId ? `${testId}-item` : undefined;
    return (
      <SimpleSelectItem key={item.value} value={item.value} data-test-id={childId}>
        {item.label}
      </SimpleSelectItem>
    );
  });

const PREVENT_CLOSE = (e: React.MouseEvent) => e.stopPropagation();
